import * as React from "react"
import { router } from "@inertiajs/react"
import { ChevronDown, ChevronUp, Plus, Sparkles, Trash2 } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { InlineField } from "@/components/ui/inline-edit"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import { INCLUDED_ICONS, resolveIcon, storedIconLabel } from "@/components/included-icons"
import { useInlineRecord } from "@/hooks/use-inline-record"
import { inlinePatch } from "@/lib/inline-save"
import type { FeatureItem, FeatureListEntry, Lang } from "./types"

const BLANK_ITEM: FeatureItem = { icon: "", title_en: "", title_es: "", desc_en: "", desc_es: "" }

/** The icon this item will draw on the page, at the page's colour. An emoji
 *  left over from before the picker existed still shows as itself. */
function IconPreview({ icon, title }: { icon: string; title: string }) {
  const { Icon, emoji } = resolveIcon(icon, title)
  return (
    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-hairline bg-surface">
      {Icon ? (
        <Icon aria-hidden className="h-6 w-6 text-teal" strokeWidth={1.25} />
      ) : (
        <span aria-hidden className="text-xl leading-none">{emoji}</span>
      )}
    </span>
  )
}

export function FeatureListEditor({
  entry,
  lang = "en",
  aiEnabled = false,
}: {
  entry: FeatureListEntry
  lang?: Lang
  aiEnabled?: boolean
}) {
  // A feature list, a career timeline and a list of commitments are the same
  // three slots wearing different names — the registry supplies the names.
  const iconLabel = entry.item_labels?.icon ?? "Icon"
  const titleLabel = entry.item_labels?.title ?? "Title"
  const descLabel = entry.item_labels?.desc ?? "Description"
  const customIcon = entry.item_labels?.icon != null
  // Lists the page draws with the shared line icons get a picker of exactly
  // those, previewed at the size and colour the page uses, so this screen
  // shows the icon the visitor will see rather than an empty emoji box.
  const lineIcons = entry.icon_set === "line"

  const [translating, setTranslating] = React.useState(false)

  // What the server holds. Every save sends the whole list, so it is built
  // from this rather than from the boxes on screen — a tick on one line can
  // never carry an unfinished edit on another.
  const items = entry.items.length > 0 ? entry.items : [ { ...BLANK_ITEM } ]

  function persist(next: FeatureItem[]) {
    return inlinePatch(`/admin/pages/sections/${encodeURIComponent(entry.slug)}`, {
      page_section: { content_en: { items: next } },
    })
  }

  // One cell of one line: "2:title_en".
  const record: Record<string, string> = Object.fromEntries(
    items.flatMap((item, index) =>
      (Object.keys(BLANK_ITEM) as (keyof FeatureItem)[]).map((field) => [
        `${index}:${field}`,
        item[field] ?? "",
      ]),
    ),
  )

  const fields = useInlineRecord({
    record,
    save: (key, value) => {
      const [ index, field ] = key.split(":")
      return persist(
        items.map((item, i) => (i === Number(index) ? { ...item, [field]: String(value) } : item)),
      )
    },
  })

  function translate() {
    setTranslating(true)
    router.post(
      "/admin/ai/translate",
      { slug: entry.slug },
      { preserveScroll: true, onFinish: () => setTranslating(false) },
    )
  }

  // Adding, removing and reordering are structural rather than typed, so they
  // commit on the click — the same idiom as the ranked photo slots. They act
  // on the saved list, so an unticked edit is left behind rather than dragged
  // into a line it no longer belongs to.
  function addItem() {
    persist([ ...items, { ...BLANK_ITEM } ])
  }

  function removeItem(index: number) {
    persist(items.filter((_, i) => i !== index))
  }

  function move(index: number, delta: number) {
    const target = index + delta
    if (target < 0 || target >= items.length) return
    const next = [ ...items ]
    ;[ next[index], next[target] ] = [ next[target], next[index] ]
    persist(next)
  }

  function cell(index: number, field: keyof FeatureItem) {
    return fields.state(`${index}:${field}`)
  }

  return (
    <div className="space-y-4">
      {lang === "es" && (
        <div className="flex flex-wrap items-center gap-3">
          {entry.es_stale ? (
            <Badge tone="signal">Spanish out of date</Badge>
          ) : (
            <Badge tone="muted">Spanish up to date</Badge>
          )}
          {aiEnabled && (
            <Button type="button" size="sm" variant="secondary" disabled={translating} onClick={translate}>
              <Sparkles className="mr-1.5 h-3.5 w-3.5" />
              {translating ? "Translating…" : "Translate from English"}
            </Button>
          )}
        </div>
      )}

      {items.map((item, index) => (
        <div key={index} className="space-y-3 rounded-lg border border-hairline p-4">
          {lang === "en" && (
            <div className="flex items-center justify-between">
              <div className="flex gap-1">
                <button
                  type="button"
                  onClick={() => move(index, -1)}
                  disabled={index === 0}
                  className="rounded p-1 text-ink-muted hover:bg-surface hover:text-ink-display disabled:opacity-30"
                  aria-label="Move earlier"
                >
                  <ChevronUp className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => move(index, 1)}
                  disabled={index === items.length - 1}
                  className="rounded p-1 text-ink-muted hover:bg-surface hover:text-ink-display disabled:opacity-30"
                  aria-label="Move later"
                >
                  <ChevronDown className="h-3.5 w-3.5" />
                </button>
              </div>
              <button
                type="button"
                onClick={() => removeItem(index)}
                className="text-ink-muted hover:text-danger-display"
                aria-label="Remove item"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          )}

          {lang === "en" ? (
            <>
              <div
                className={
                  lineIcons
                    ? "grid gap-3 sm:grid-cols-[260px_1fr]"
                    : customIcon
                      ? "grid gap-3 sm:grid-cols-[140px_1fr]"
                      : "grid gap-3 sm:grid-cols-[80px_1fr]"
                }
              >
                <InlineField label={iconLabel} htmlFor={`${entry.id}-${index}-icon`} state={cell(index, "icon")}>
                  {lineIcons ? (
                    <div className="flex items-center gap-3">
                      <IconPreview icon={cell(index, "icon").value} title={cell(index, "title_en").value} />
                      <Select
                        id={`${entry.id}-${index}-icon`}
                        value={cell(index, "icon").value}
                        onChange={(e) => fields.set(`${index}:icon`, e.target.value)}
                        onKeyDown={cell(index, "icon").keys}
                      >
                        <option value="">Auto — from the title</option>
                        {INCLUDED_ICONS.map(({ name, label }) => (
                          <option key={name} value={name}>{label}</option>
                        ))}
                        {/* A value the picker has no option for — an emoji
                            from before it existed — stays selectable so
                            opening this screen can't silently swap it. It is
                            named by the icon it draws, which for most emoji
                            is now one of the line icons. */}
                        {item.icon !== "" && !INCLUDED_ICONS.some((i) => i.name === item.icon) && (
                          <option value={item.icon}>{storedIconLabel(item.icon)}</option>
                        )}
                      </Select>
                    </div>
                  ) : (
                    <Input
                      id={`${entry.id}-${index}-icon`}
                      value={cell(index, "icon").value}
                      onChange={(e) => fields.set(`${index}:icon`, e.target.value)}
                      onKeyDown={cell(index, "icon").keys}
                      placeholder={customIcon ? "" : "🚗"}
                    />
                  )}
                </InlineField>
                <InlineField
                  label={`${titleLabel} (English)`}
                  htmlFor={`${entry.id}-${index}-title-en`}
                  state={cell(index, "title_en")}
                >
                  <Input
                    id={`${entry.id}-${index}-title-en`}
                    value={cell(index, "title_en").value}
                    onChange={(e) => fields.set(`${index}:title_en`, e.target.value)}
                    onKeyDown={cell(index, "title_en").keys}
                  />
                </InlineField>
              </div>
              <InlineField
                label={`${descLabel} (English)`}
                htmlFor={`${entry.id}-${index}-desc-en`}
                state={cell(index, "desc_en")}
              >
                <Input
                  id={`${entry.id}-${index}-desc-en`}
                  value={cell(index, "desc_en").value}
                  onChange={(e) => fields.set(`${index}:desc_en`, e.target.value)}
                  onKeyDown={cell(index, "desc_en").keys}
                />
              </InlineField>
            </>
          ) : (
            <>
              <InlineField
                label={`${titleLabel} (Spanish)`}
                htmlFor={`${entry.id}-${index}-title-es`}
                state={cell(index, "title_es")}
                above={item.title_en !== "" ? <p className="text-sm text-ink-muted">{item.title_en}</p> : undefined}
              >
                <Input
                  id={`${entry.id}-${index}-title-es`}
                  value={cell(index, "title_es").value}
                  onChange={(e) => fields.set(`${index}:title_es`, e.target.value)}
                  onKeyDown={cell(index, "title_es").keys}
                />
              </InlineField>
              <InlineField
                label={`${descLabel} (Spanish)`}
                htmlFor={`${entry.id}-${index}-desc-es`}
                state={cell(index, "desc_es")}
                above={item.desc_en !== "" ? <p className="text-sm text-ink-muted">{item.desc_en}</p> : undefined}
              >
                <Input
                  id={`${entry.id}-${index}-desc-es`}
                  value={cell(index, "desc_es").value}
                  onChange={(e) => fields.set(`${index}:desc_es`, e.target.value)}
                  onKeyDown={cell(index, "desc_es").keys}
                />
              </InlineField>
            </>
          )}
        </div>
      ))}

      {lang === "en" && (
        <Button type="button" variant="secondary" size="sm" onClick={addItem}>
          <Plus className="mr-1.5 h-3.5 w-3.5" />
          Add item
        </Button>
      )}
    </div>
  )
}
